// TESSERAX — LE PLI (la 4ᵉ dimension) : double tranche couplée + résolution par UNION. Cœur PUR, testé.
//
// TESSERAX = tesseract = hypercube 4D. Le jeu de base est 3D (un puits 7×7×14). « Le Pli »
// est le frère dimensionnel supérieur de la Bascule : le puits devient DEUX tranches 7×7×14
// SUPERPOSÉES — ENDROIT et ENVERS, deux projections d'un même hypercube. Le joueur empile dans
// la tranche ACTIVE ; l'autre est visible en fantôme translucide superposé. Le Pli fait pivoter
// l'hypercube à travers le 4ᵉ axe (ana/kata) : il ÉCHANGE la tranche active, pour aller combler
// dans l'AUTRE cube les trous laissés dans le premier.
//
// MODÈLE D'EFFACEMENT — L'UNION (cf. décision produit) : les deux cubes se RECOUVRENT. Une rangée
// (à y,z fixes sur l'axe X, ou y,x sur l'axe Z) s'efface dès que l'UNION des deux tranches couvre
// ses 7 cases — chaque case remplie dans AU MOINS une tranche. On efface alors la rangée dans LES
// DEUX cubes, puis gravité verticale indépendante par cube (cascade). Deux conséquences élégantes :
//   · une rangée pleine dans UNE seule tranche s'efface comme un clear normal (la boucle de base,
//     familière) — l'union la couvre trivialement ;
//   · une rangée que NI l'une NI l'autre ne complète seule, mais que les deux complètent ENSEMBLE,
//     est une TESSERA — le feat 4D impossible dans un cube unique, par-dessus la Croix. Couche de
//     maîtrise par-dessus la boucle de base : facile à apprendre, difficile à maîtriser.
//
// CHOIX D'ÉTAT (cf. plan) : deux `Grid` indépendantes (et non un 4ᵉ indice grid[w][x][y][z]). Toutes
// les primitives de `grid.ts` restent utilisables PAR TRANCHE sans la moindre retouche (la base reste
// bit-exacte). PUR & SANS RNG : aucune de ces fonctions ne consomme un tirage → la séquence de pièces
// n'est jamais perturbée, donc le défi du jour reste déterministe et le rejeu/verify bit-exact.
import { GRID, HEIGHT } from './constants';
import type { Grid } from './grid';
import { settleDown } from './grid';

// La tranche active : celle où tombe la pièce et où agissent gravité / Bascule.
export type Slice = 'endroit' | 'envers';
export interface Dual { e: Grid; v: Grid; active: Slice; }
export function otherSlice(s: Slice): Slice { return s === 'endroit' ? 'envers' : 'endroit'; }

// PLI-ÉCHANGE (foldSwap) : bascule la tranche active (rotation de l'hypercube par ana/kata — le
// joueur prend le contrôle de l'AUTRE cube pour y combler les trous du premier). Permutation PURE
// et INVOLUTIVE : `foldSwap(foldSwap(d))` ≡ `d`. Aucune matière déplacée — l'union étant symétrique,
// un échange ne crée ni ne détruit d'effacement par lui-même ; le Pli est un coup de NAVIGATION
// (les Tesseras se déclenchent au verrouillage, quand l'union d'une rangée se complète).
export function foldSwap(d: Dual): Dual { return { e: d.e, v: d.v, active: otherSlice(d.active) }; }

/* ======================= recouvrement par UNION ======================= */
// Case (x,y,z) couverte par l'union des deux tranches ?
function cov(e: Grid, v: Grid, x: number, y: number, z: number): boolean { return e[x][y][z] !== null || v[x][y][z] !== null; }

// Une rangée X (à y,z fixes) : couverte par l'union ? pleine dans une seule tranche ?
function unionFullX(e: Grid, v: Grid, y: number, z: number): boolean { for (let x = 0; x < GRID; x++) if (!cov(e, v, x, y, z)) return false; return true; }
function sliceFullX(g: Grid, y: number, z: number): boolean { for (let x = 0; x < GRID; x++) if (g[x][y][z] === null) return false; return true; }
function unionFullZ(e: Grid, v: Grid, y: number, x: number): boolean { for (let z = 0; z < GRID; z++) if (!cov(e, v, x, y, z)) return false; return true; }
function sliceFullZ(g: Grid, y: number, x: number): boolean { for (let z = 0; z < GRID; z++) if (g[x][y][z] === null) return false; return true; }
function unionFullLayer(e: Grid, v: Grid, y: number): boolean { for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) if (!cov(e, v, x, y, z)) return false; return true; }
function sliceFullLayer(g: Grid, y: number): boolean { for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) if (g[x][y][z] === null) return false; return true; }

// Une rangée effacée par l'union. `tessera` = a EXIGÉ les deux tranches (aucune ne la complétait seule).
export interface UnionRow { axis: 'x' | 'z'; y: number; fixed: number; tessera: boolean; }
export interface UnionEval {
  layers: number[];        // étages pleins par l'union
  rows: UnionRow[];        // rangées pleines par l'union (hors étages pleins)
  rowCount: number; xRows: number; zRows: number;
  tesseraRows: number;     // sous-ensemble des rangées qui ont EXIGÉ les deux tranches
  tesseraLayers: number;   // étages qui ont exigé les deux tranches
}

// DÉTECTION (pure, n'altère rien) : rangées/étages pleins par l'UNION des deux tranches, avec le
// marquage TESSERA. Miroir exact de `evaluateClears`, mais sur la couverture-union au lieu d'une grille.
// PERF-CORE-002 : une seule passe par étage/rangée fusionne le test union-fullness et le test
// tessera (ni e ni v ne couvre seul) — élimine les 2 passes sliceFullX/Z supplémentaires par
// rangée pleine. Plus d'allocation `fset` : on utilise layers.includes(y) (HEIGHT≤14).
export function evaluateUnion(e: Grid, v: Grid): UnionEval {
  const layers: number[] = []; let tesseraLayers = 0;
  // Passe étages : union-plein + tessera en une seule boucle.
  for (let y = 0; y < HEIGHT; y++) {
    let eAll = true, vAll = true, covAll = true;
    outer: for (let x = 0; x < GRID; x++) {
      for (let z = 0; z < GRID; z++) {
        const eHas = e[x][y][z] !== null, vHas = v[x][y][z] !== null;
        if (!eHas) eAll = false;
        if (!vHas) vAll = false;
        if (!eHas && !vHas) { covAll = false; break outer; }
      }
    }
    if (covAll) { layers.push(y); if (!eAll && !vAll) tesseraLayers++; }
  }
  const rows: UnionRow[] = []; let xRows = 0, zRows = 0, tesseraRows = 0;
  // Passe rangées : une seule boucle par rangée pour union-fullness + tessera.
  for (let y = 0; y < HEIGHT; y++) {
    if (layers.includes(y)) continue;
    for (let z = 0; z < GRID; z++) {
      let eAll = true, vAll = true, covAll = true;
      for (let x = 0; x < GRID; x++) {
        const eHas = e[x][y][z] !== null, vHas = v[x][y][z] !== null;
        if (!eHas) eAll = false;
        if (!vHas) vAll = false;
        if (!eHas && !vHas) { covAll = false; break; }
      }
      if (covAll) { const t = !eAll && !vAll; rows.push({ axis: 'x', y, fixed: z, tessera: t }); xRows++; if (t) tesseraRows++; }
    }
    for (let x = 0; x < GRID; x++) {
      let eAll = true, vAll = true, covAll = true;
      for (let z = 0; z < GRID; z++) {
        const eHas = e[x][y][z] !== null, vHas = v[x][y][z] !== null;
        if (!eHas) eAll = false;
        if (!vHas) vAll = false;
        if (!eHas && !vHas) { covAll = false; break; }
      }
      if (covAll) { const t = !eAll && !vAll; rows.push({ axis: 'z', y, fixed: x, tessera: t }); zRows++; if (t) tesseraRows++; }
    }
  }
  return { layers, rows, rowCount: xRows + zRows, xRows, zRows, tesseraRows, tesseraLayers };
}

// Couverture-union d'une rangée (nombre de cases couvertes par au moins une tranche).
function unionCovX(e: Grid, v: Grid, y: number, z: number): number { let n = 0; for (let x = 0; x < GRID; x++) if (cov(e, v, x, y, z)) n++; return n; }
function unionCovZ(e: Grid, v: Grid, y: number, x: number): number { let n = 0; for (let z = 0; z < GRID; z++) if (cov(e, v, x, y, z)) n++; return n; }

// Rangées à UNE case de s'effacer par l'union (couverture = GRID-1) — l'indice de lisibilité
// « Tessera imminente » côté rendu (cf. main.ts) : on n'éclaire que les rangées ACTIONNABLES, pour
// transformer la superposition des deux cubes en SIGNAL plutôt qu'en bruit. Pur, n'altère rien.
export function tesseraPending(e: Grid, v: Grid): UnionRow[] {
  const out: UnionRow[] = [];
  for (let y = 0; y < HEIGHT; y++) {
    for (let z = 0; z < GRID; z++) if (unionCovX(e, v, y, z) === GRID - 1) out.push({ axis: 'x', y, fixed: z, tessera: true });
    for (let x = 0; x < GRID; x++) if (unionCovZ(e, v, y, x) === GRID - 1) out.push({ axis: 'z', y, fixed: x, tessera: true });
  }
  return out;
}

export interface UnionResolve { chain: number; rowCount: number; xRows: number; zRows: number; slabs: number; tesseraRows: number; cross: boolean; cells: number[][]; }

// RÉSOLUTION (mute LES DEUX grilles en place) : tant que l'union efface quelque chose, on retire la
// rangée/l'étage des DEUX tranches, on tasse verticalement chaque cube indépendamment, et on
// recommence (cascade) — miroir de `resolveBoard` côté grille unique. Renvoie le butin agrégé :
// `tesseraRows` = rangées ayant exigé les deux cubes, `cross` = au moins une rangée X ET une Z dans la
// même passe (la « Croix » du puits 4D). `cells` = cellules retirées [x,y,z,color] (pour le juice).
export function resolveUnion(e: Grid, v: Grid): UnionResolve {
  let chain = 0, rowCount = 0, xRows = 0, zRows = 0, slabs = 0, tesseraRows = 0; let cross = false; const cells: number[][] = [];
  while (true) {
    const u = evaluateUnion(e, v);
    if (u.rowCount === 0 && u.layers.length === 0) break;
    chain++;
    const strike = (x: number, y: number, z: number) => {
      if (e[x][y][z] !== null) { cells.push([x, y, z, e[x][y][z]!]); e[x][y][z] = null; }
      if (v[x][y][z] !== null) { cells.push([x, y, z, v[x][y][z]!]); v[x][y][z] = null; }
    };
    for (let i = 0; i < u.rows.length; i++) { const r = u.rows[i]; if (r.axis === 'x') { for (let x = 0; x < GRID; x++) strike(x, r.y, r.fixed); } else { for (let z = 0; z < GRID; z++) strike(r.fixed, r.y, z); } }
    for (let i = 0; i < u.layers.length; i++) { const y = u.layers[i]; for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) strike(x, y, z); }
    settleDown(e); settleDown(v);
    rowCount += u.rowCount; xRows += u.xRows; zRows += u.zRows; slabs += u.layers.length; tesseraRows += u.tesseraRows;
    if (u.xRows > 0 && u.zRows > 0) cross = true;
  }
  return { chain, rowCount, xRows, zRows, slabs, tesseraRows, cross, cells };
}
